import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  IndianRupee, Plus, Search, Download, Trash2, Pencil, Loader2, AlertTriangle,
  ChevronLeft, ChevronRight, CheckCircle2, Banknote, Wallet, Wallet2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const formatINR = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}₹${Math.abs(v).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`;
};

const todayStr = () => new Date().toISOString().split("T")[0];

const emptyForm = () => ({
  client_id: "",
  amount: "",
  date: todayStr(),
  mode: "Bank",
  transaction_reference: "",
  invoice_id: "",
  notes: "",
});

export default function ReceiptsPage() {
  const [receipts, setReceipts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modal state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [openInvoices, setOpenInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Data loading ──────────────────────────────────────────────────────────
  const fetchAll = async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/receipts?limit=1000"),
        api.get("/clients"),
      ]);
      setReceipts(r1.data || []);
      setClients(r2.data || []);
    } catch {
      toast.error("Failed to load receipts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { setPage(1); }, [search, clientFilter, modeFilter, dateFrom, dateTo, pageSize]);

  // When client changes in the modal, fetch their open invoices for the manual
  // allocation picker
  useEffect(() => {
    if (!dialogOpen || !form.client_id) {
      setOpenInvoices([]);
      return;
    }
    setLoadingInvoices(true);
    api.get(`/clients/${form.client_id}/open-invoices`)
      .then((r) => setOpenInvoices(r.data || []))
      .catch(() => setOpenInvoices([]))
      .finally(() => setLoadingInvoices(false));
  }, [form.client_id, dialogOpen]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let items = receipts;
    if (clientFilter) items = items.filter((r) => r.client_id === clientFilter);
    if (modeFilter) items = items.filter((r) => r.mode === modeFilter);
    if (dateFrom) items = items.filter((r) => r.date >= dateFrom);
    if (dateTo) items = items.filter((r) => r.date <= dateTo);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter((r) =>
        (r.client_name || "").toLowerCase().includes(q) ||
        (r.transaction_reference || "").toLowerCase().includes(q) ||
        (r.notes || "").toLowerCase().includes(q) ||
        (r.allocations || []).some((a) => (a.invoice_number || "").toLowerCase().includes(q))
      );
    }
    return items;
  }, [receipts, clientFilter, modeFilter, dateFrom, dateTo, search]);

  const stats = useMemo(() => {
    const total = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);
    const cash = filtered.filter((r) => r.mode === "Cash").reduce((s, r) => s + Number(r.amount || 0), 0);
    const bank = filtered.filter((r) => r.mode === "Bank").reduce((s, r) => s + Number(r.amount || 0), 0);
    const advance = filtered.reduce((s, r) => s + Number(r.advance_added || 0), 0);
    return { total, count: filtered.length, cash, bank, advance };
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const pagedReceipts = filtered.slice(startIdx, startIdx + pageSize);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.receipt_id);
    setForm({
      client_id: r.client_id || "",
      amount: r.amount || "",
      date: r.date || todayStr(),
      mode: r.mode || "Bank",
      transaction_reference: r.transaction_reference || "",
      invoice_id: "",
      notes: r.notes || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.client_id) return toast.error("Please choose a client");
    const amt = Number(form.amount);
    if (!amt || amt <= 0) return toast.error("Amount must be greater than zero");
    setSaving(true);
    try {
      const payload = {
        client_id: form.client_id,
        amount: amt,
        date: form.date,
        mode: form.mode,
        transaction_reference: form.transaction_reference || "",
        notes: form.notes || "",
      };
      if (form.invoice_id) payload.invoice_id = form.invoice_id;

      const r = editingId
        ? await api.put(`/receipts/${editingId}`, payload)
        : await api.post("/receipts", payload);

      const summary = r.data?.receipt;
      const applied = (summary?.allocations || []).reduce((s, a) => s + Number(a.amount_applied || 0), 0);
      const advance = Number(summary?.advance_added || 0);
      const allocCount = (summary?.allocations || []).length;
      const bits = [];
      if (allocCount) bits.push(`Applied ${formatINR(applied)} across ${allocCount} invoice(s)`);
      if (advance > 0.01) bits.push(`${formatINR(advance)} → advance`);
      toast.success(
        editingId
          ? `Receipt updated. ${bits.join(" · ")}`
          : `Receipt saved. ${bits.join(" · ") || "No allocations matched."}`
      );
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/receipts/${deleteTarget.receipt_id}`);
      toast.success("Receipt reversed");
      setReceipts((prev) => prev.filter((r) => r.receipt_id !== deleteTarget.receipt_id));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reverse failed");
    } finally {
      setDeleting(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (clientFilter) params.set("client_id", clientFilter);
      if (modeFilter) params.set("mode", modeFilter);
      if (dateFrom) params.set("from_date", dateFrom);
      if (dateTo) params.set("to_date", dateTo);
      const r = await api.get(`/receipts/export.csv?${params.toString()}`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `receipts_${todayStr()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="receipts-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Receipts</h1>
          <p className="text-sm text-[#64748b] mt-1">Money received from clients. FIFO-allocated against outstanding invoices.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={downloadCsv} disabled={loading || filtered.length === 0}
            data-testid="export-receipts-csv-btn" className="border-[#e2e8f0] rounded-xl">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={openCreate} data-testid="record-receipt-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl">
            <Plus className="h-4 w-4 mr-2" /> Record Receipt
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-white border-[#e2e8f0]"><CardContent className="p-4">
          <p className="text-[10px] text-[#64748b] uppercase tracking-wider font-medium flex items-center gap-1"><IndianRupee className="h-3 w-3" /> Total Received</p>
          <p className="font-outfit text-xl font-bold text-[#0f172a] mt-1" data-testid="stat-total-received">{formatINR(stats.total)}</p>
          <p className="text-[11px] text-[#94a3b8] mt-0.5">{stats.count} receipt{stats.count !== 1 ? "s" : ""}</p>
        </CardContent></Card>
        <Card className="bg-white border-[#10b981]/20"><CardContent className="p-4">
          <p className="text-[10px] text-[#10b981] uppercase tracking-wider font-medium flex items-center gap-1"><Banknote className="h-3 w-3" /> Bank</p>
          <p className="font-outfit text-xl font-bold text-[#10b981] mt-1" data-testid="stat-bank">{formatINR(stats.bank)}</p>
        </CardContent></Card>
        <Card className="bg-white border-[#f59e0b]/20"><CardContent className="p-4">
          <p className="text-[10px] text-[#f59e0b] uppercase tracking-wider font-medium flex items-center gap-1"><Wallet className="h-3 w-3" /> Cash</p>
          <p className="font-outfit text-xl font-bold text-[#f59e0b] mt-1" data-testid="stat-cash">{formatINR(stats.cash)}</p>
        </CardContent></Card>
        <Card className="bg-white border-[#7c3aed]/20"><CardContent className="p-4">
          <p className="text-[10px] text-[#7c3aed] uppercase tracking-wider font-medium flex items-center gap-1"><Wallet2 className="h-3 w-3" /> Advance Held</p>
          <p className="font-outfit text-xl font-bold text-[#7c3aed] mt-1" data-testid="stat-advance">{formatINR(stats.advance)}</p>
        </CardContent></Card>
      </div>

      {/* Filter bar */}
      <Card className="bg-white border-[#e2e8f0]"><CardContent className="p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search client / reference / invoice no…"
              data-testid="receipts-search" className="pl-9 h-9 border-[#e2e8f0]" />
          </div>
          <Select value={clientFilter || "all"} onValueChange={(v) => setClientFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-44 h-9 border-[#e2e8f0]" data-testid="filter-client"><SelectValue placeholder="All clients" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={modeFilter || "all"} onValueChange={(v) => setModeFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-32 h-9 border-[#e2e8f0]" data-testid="filter-mode"><SelectValue placeholder="All modes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modes</SelectItem>
              <SelectItem value="Bank">Bank</SelectItem>
              <SelectItem value="Cash">Cash</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36 h-9 border-[#e2e8f0]" data-testid="filter-from" />
          <span className="text-xs text-[#94a3b8]">to</span>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36 h-9 border-[#e2e8f0]" data-testid="filter-to" />
        </div>
      </CardContent></Card>

      {/* Table */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm"><CardContent className="p-0">
        {loading ? (
          <div className="p-12 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4338ca]" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center">
            <IndianRupee className="h-12 w-12 text-[#e2e8f0] mx-auto mb-3" />
            <p className="text-sm font-medium text-[#0f172a] mb-1">{receipts.length === 0 ? "Record your first receipt" : "No receipts match these filters."}</p>
            <p className="text-xs text-[#64748b] mb-4">{receipts.length === 0 ? "Every payment you record is auto-allocated against the client's oldest invoices." : "Try clearing the filters above."}</p>
            {receipts.length === 0 && (
              <Button onClick={openCreate} className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl" data-testid="empty-record-btn">
                <Plus className="h-4 w-4 mr-2" /> Record Receipt
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Date</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Client</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Mode</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Reference</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Amount</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Allocated To</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedReceipts.map((r) => (
                  <tr key={r.receipt_id} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`receipt-row-${r.receipt_id}`}>
                    <td className="py-3 px-4 text-[#64748b] text-xs">{r.date}</td>
                    <td className="py-3 px-4 text-[#0f172a] font-medium">{r.client_name || "-"}</td>
                    <td className="py-3 px-4">
                      <Badge className={
                        r.mode === "Bank"
                          ? "bg-[#10b981]/10 text-[#10b981] text-[10px]"
                          : "bg-[#f59e0b]/10 text-[#f59e0b] text-[10px]"
                      }>{r.mode === "Bank" ? <Banknote className="h-3 w-3 mr-1" /> : <Wallet className="h-3 w-3 mr-1" />}{r.mode}</Badge>
                    </td>
                    <td className="py-3 px-4 text-[#64748b] font-mono text-xs">{r.transaction_reference || "-"}</td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-[#0f172a]">{formatINR(r.amount)}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1.5 items-center">
                        {(r.allocations || []).map((a) => (
                          <Badge key={a.invoice_id} className="bg-[#4338ca]/10 text-[#4338ca] text-[10px] font-mono" title={`Applied ${formatINR(a.amount_applied)}`}>
                            {a.invoice_status_after === "paid" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                            {a.invoice_number || a.invoice_id}: {formatINR(a.amount_applied)}
                          </Badge>
                        ))}
                        {(r.advance_added || 0) > 0.01 && (
                          <Badge className="bg-[#7c3aed]/10 text-[#7c3aed] text-[10px]" title="Surplus held as advance">
                            <Sparkles className="h-3 w-3 mr-1" /> {formatINR(r.advance_added)} advance
                          </Badge>
                        )}
                        {(r.allocations || []).length === 0 && (r.advance_added || 0) < 0.01 && (
                          <span className="text-[10px] text-[#94a3b8]">-</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(r)} className="h-7 w-7 p-0" title="Edit" data-testid={`edit-receipt-${r.receipt_id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(r)} className="h-7 w-7 p-0 text-[#ef4444] hover:bg-[#ef4444]/10" title="Reverse receipt" data-testid={`delete-receipt-${r.receipt_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination bar */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#f1f5f9] bg-[#fafbfc]" data-testid="receipts-pagination">
              <div className="text-xs text-[#64748b]">
                Viewing {startIdx + 1}-{Math.min(startIdx + pagedReceipts.length, filtered.length)} of {filtered.length}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#94a3b8]">Rows</span>
                  <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                    <SelectTrigger className="h-7 w-[70px] border-[#e2e8f0] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="h-7 px-2 rounded-md border-[#e2e8f0] text-xs" data-testid="receipts-prev">
                    <ChevronLeft className="h-3 w-3 mr-0.5" /> Previous
                  </Button>
                  <span className="text-xs text-[#64748b] px-2">Page {safePage} of {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="h-7 px-2 rounded-md border-[#e2e8f0] text-xs" data-testid="receipts-next">
                    Next <ChevronRight className="h-3 w-3 ml-0.5" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent></Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => !open && !saving && setDialogOpen(false)}>
        <DialogContent className="max-w-md" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg flex items-center gap-2">
              <IndianRupee className="h-4 w-4 text-[#4338ca]" /> {editingId ? "Edit Receipt" : "Record Receipt"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Original allocations are reversed and the new values are re-applied via FIFO."
                : "Allocates oldest invoices first. Excess becomes an advance balance."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs font-medium">Client *</Label>
              <Select value={form.client_id || ""} onValueChange={(v) => setForm({ ...form, client_id: v, invoice_id: "" })}>
                <SelectTrigger className="mt-1 border-[#e2e8f0] h-9" data-testid="form-client"><SelectValue placeholder="Choose client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Amount (INR) *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00" data-testid="form-amount" className="mt-1 border-[#e2e8f0] h-9 font-mono" />
              </div>
              <div>
                <Label className="text-xs font-medium">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="form-date" className="mt-1 border-[#e2e8f0] h-9" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Mode</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger className="mt-1 border-[#e2e8f0] h-9" data-testid="form-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank">Bank</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium">Reference</Label>
                <Input value={form.transaction_reference} onChange={(e) => setForm({ ...form, transaction_reference: e.target.value })}
                  placeholder="UTR / Cheque no" data-testid="form-ref" className="mt-1 border-[#e2e8f0] h-9 font-mono text-xs" />
              </div>
            </div>

            {/* Manual invoice allocation */}
            {form.client_id && (
              <div>
                <Label className="text-xs font-medium flex items-center gap-1.5">
                  Apply first to invoice (optional)
                  <span className="text-[10px] text-[#94a3b8] font-normal">— leave empty for FIFO</span>
                </Label>
                {loadingInvoices ? (
                  <div className="mt-1 h-9 flex items-center px-3 border border-[#e2e8f0] rounded-md text-xs text-[#94a3b8]">
                    <Loader2 className="h-3 w-3 animate-spin mr-2" /> Loading open invoices…
                  </div>
                ) : (
                  <Select value={form.invoice_id || "auto"} onValueChange={(v) => setForm({ ...form, invoice_id: v === "auto" ? "" : v })}>
                    <SelectTrigger className="mt-1 border-[#e2e8f0] h-9" data-testid="form-invoice">
                      <SelectValue placeholder={openInvoices.length === 0 ? "No open invoices" : "Auto (FIFO — oldest first)"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (FIFO — oldest first)</SelectItem>
                      {openInvoices.map((inv) => (
                        <SelectItem key={inv.invoice_id} value={inv.invoice_id}>
                          {inv.invoice_number || inv.invoice_id} · {inv.date} · {formatINR(inv.balance_due)} due
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            <div>
              <Label className="text-xs font-medium">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2} placeholder="Internal note (optional)" data-testid="form-notes" className="mt-1 border-[#e2e8f0]" />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}
                className="rounded-lg border-[#e2e8f0]" data-testid="cancel-receipt-btn">Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#4338ca] hover:bg-[#3730a3] text-white rounded-lg" data-testid="save-receipt-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                {editingId ? "Update Receipt" : "Save Receipt"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reverse / delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <DialogContent className="max-w-md" data-testid="delete-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg flex items-center gap-2 text-[#ef4444]">
              <AlertTriangle className="h-5 w-5" /> Reverse this receipt?
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-4 mt-2">
              <p className="text-sm text-[#475569]">
                Reversing this receipt will restore <span className="font-semibold text-[#0f172a]">{formatINR(deleteTarget.amount)}</span> to {deleteTarget.client_name}'s outstanding balance
                {(deleteTarget.advance_added || 0) > 0.01 && <> and roll back <span className="font-semibold">{formatINR(deleteTarget.advance_added)}</span> of advance held</>}.
              </p>
              <div className="bg-[#fef2f2] border border-[#ef4444]/20 rounded-lg p-3 text-xs text-[#7f1d1d]">
                {(deleteTarget.allocations || []).length > 0 ? (
                  <>
                    <p className="font-semibold mb-1">Invoices that will be re-opened:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {(deleteTarget.allocations || []).map((a) => (
                        <li key={a.invoice_id}>{a.invoice_number} ({formatINR(a.amount_applied)})</li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <p>This receipt only contributed to advance balance. Nothing to re-open on the invoice side.</p>
                )}
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}
                  className="rounded-lg border-[#e2e8f0]" data-testid="cancel-delete-receipt-btn">Cancel</Button>
                <Button onClick={confirmDelete} disabled={deleting} className="bg-[#ef4444] hover:bg-[#dc2626] text-white rounded-lg" data-testid="confirm-delete-receipt-btn">
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Reverse Receipt
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
